import Peer from '@bowbee/peer-lite';
import type { PlaycastMessage, PlaycastMessageTarget } from '@playcastdotio/Messaging';
import type { PeerChunkedMessage, PeerMessageBuffer } from './types';

// Types
type PendingMessage = {
  message: PlaycastMessage<PlaycastMessageTarget>;
  channelLabel: string;
  retry: number;
};

let CHUNK_SIZE = 16384; // Exactly Firefox/Safari limit
const CHUNK_TIMEOUT = 60000; // 60 seconds for large messages
const RETRY_INTERVAL = 200; // 200ms between retries
const MAX_RETRIES = 5;
let hasDetectedMaxSize = false;

// Debug flag - can be toggled via setDebug
let isDebugEnabled = false;

// Debug logging function
const debugLog = (prefix: string, ...args: any[]) => {
  if (isDebugEnabled) {
    console.log(`[WebRTC-Chunk][${prefix}]`, ...args);
  }
};

// State containers
const messageBuffers: Record<string, PeerMessageBuffer> = {};
const pendingMessages: PendingMessage[] = new Array<PendingMessage>();
let retryIntervalId: NodeJS.Timeout | undefined;

// Helper functions
const encoder = new TextEncoder();
const decoder = new TextDecoder();
const generateMessageId = (): string => `${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
// Convert message to binary format
const packMessage = (message: any): Uint8Array => {
  const messageStr = JSON.stringify(message);
  return encoder.encode(messageStr);
};

// Convert binary back to message
const unpackMessage = (data: Uint8Array): any => {
  debugLog('UNPACK', `Unpacking message of size ${data.byteLength} bytes`);
  const messageStr = decoder.decode(data);
  return JSON.parse(messageStr);
};

const parseMessage = (data: any): any | undefined => {
  try {
    if (data instanceof ArrayBuffer) {
      const decoder = new TextDecoder('utf-8');
      return JSON.parse(decoder.decode(data));
    }
    if (typeof data === 'string') {
      return JSON.parse(data);
    }
    return data;
  } catch (error) {
    debugLog('ERROR', 'Failed to parse message:', error);
    return undefined;
  }
};

const clearMessageBuffer = (messageId: string): void => {
  const buffer = messageBuffers[messageId];
  if (buffer?.timer) {
    clearTimeout(buffer.timer);
    debugLog('CLEANUP', `Cleared timer for message: ${messageId}`);
  }
  delete messageBuffers[messageId];
  debugLog('CLEANUP', `Cleared buffer for message: ${messageId}`);
};

const addToPendingMessages = (message: PlaycastMessage<PlaycastMessageTarget>, channelLabel: string): void => {
  pendingMessages.push({
    message,
    channelLabel,
    retry: 0,
  });
  debugLog('PENDING', `Added message to pending queue for channel: ${channelLabel}`);
};

// Core chunking functions
const createBinaryChunk = (messageId: string, index: number, total: number, data: Uint8Array): Uint8Array => {
  // Create header with metadata
  const header = {
    id: messageId,
    index,
    total,
    size: data.byteLength,
  };
  const headerStr = JSON.stringify(header);
  const headerBin = encoder.encode(headerStr);

  // Create a combined buffer: [header size (4 bytes)][header][data]
  const headerSize = headerBin.byteLength;
  const combined = new Uint8Array(4 + headerSize + data.byteLength);

  // Write header size as 32-bit integer
  new DataView(combined.buffer).setUint32(0, headerSize);

  // Copy header and data
  combined.set(headerBin, 4);
  combined.set(data, 4 + headerSize);

  return combined;
};

// Parse a binary chunk
const parseBinaryChunk = (data: Uint8Array): { header: any; data: Uint8Array } | undefined => {
  try {
    // Read header size (first 4 bytes)
    const headerSize = new DataView(data.buffer).getUint32(0);

    // Extract and parse header
    const headerBin = data.slice(4, 4 + headerSize);
    const header = JSON.parse(decoder.decode(headerBin));

    // Extract data
    const chunkData = data.slice(4 + headerSize);

    return { header, data: chunkData };
  } catch (error) {
    debugLog('ERROR', 'Failed to parse binary chunk:', error);
    return undefined;
  }
};

const logBufferStatus = (buffer: PeerMessageBuffer, messageId: string) => {
  const missing: Array<number> = [];
  for (let i = 0; i < buffer.total; i++) {
    if (!buffer.chunks[i]) {
      missing.push(i);
    }
  }
  debugLog(
    'BUFFER-STATUS',
    `Message ${messageId}: ` +
      `${buffer.receivedChunks}/${buffer.total} chunks, ` +
      `Missing chunks: ${missing.length > 0 ? missing.join(',') : 'none'}`,
  );
};

const detectMaxMessageSize = (rawPeer: Peer): number => {
  const pc = rawPeer.get(); // Get the RTCPeerConnection
  const sctp = pc.sctp;

  if (!sctp) {
    debugLog('ERROR', 'No SCTP transport found, falling back to safe minimum');
    return 16384; // Safe fallback to Firefox/Safari minimum
  }

  const maxMessageSize = sctp.maxMessageSize;
  debugLog('DETECT', `SCTP transport reports maxMessageSize: ${maxMessageSize} bytes`);

  // Apply a safety margin (90%) and ensure we never go below minimum safe size
  const safeSize = Math.max(16384, Math.floor(maxMessageSize * 0.9));

  debugLog('DETECT', `Setting chunk size to ${safeSize} bytes (90% of ${maxMessageSize})`);
  return safeSize;
};

// Modify handleChunk to add validation and better logging:
const handleChunk = (chunk: PeerChunkedMessage, channelLabel: string): any => {
  debugLog('RECEIVE', `Received chunk ${chunk.index + 1}/${chunk.total} for message: ${chunk.id}`);

  // Validate chunk
  if (chunk.index >= chunk.total) {
    debugLog('ERROR', `Invalid chunk index ${chunk.index} >= total ${chunk.total}`);
    return undefined;
  }

  // Initialize buffer if it doesn't exist
  if (!messageBuffers[chunk.id]) {
    debugLog('BUFFER-CREATE', `Creating new buffer for message: ${chunk.id}`);
    messageBuffers[chunk.id] = {
      chunks: new Array(chunk.total),
      receivedChunks: 0,
      total: chunk.total,
      channelLabel,
      timer: setTimeout(() => {
        debugLog('TIMEOUT', `Message ${chunk.id} timed out`);
        logBufferStatus(messageBuffers[chunk.id], chunk.id);
        clearMessageBuffer(chunk.id);
      }, CHUNK_TIMEOUT),
    };
  }

  const buffer = messageBuffers[chunk.id];

  // Only process if we haven't received this chunk before
  if (!buffer.chunks[chunk.index]) {
    buffer.chunks[chunk.index] = new Uint8Array(chunk.data);
    buffer.receivedChunks++;

    debugLog('BUFFER-UPDATE', `Message: ${chunk.id}, ` + `Received: ${buffer.receivedChunks}/${buffer.total} chunks`);

    // Periodically log buffer status for large messages
    if (buffer.total > 50 && buffer.receivedChunks % 50 === 0) {
      logBufferStatus(buffer, chunk.id);
    }

    // Check if message is complete
    if (buffer.receivedChunks === buffer.total) {
      debugLog('REASSEMBLE', `Reassembling complete message: ${chunk.id}`);

      // Verify we have all chunks before joining
      const missingChunks = buffer.chunks.findIndex((c) => !c);
      if (missingChunks !== -1) {
        debugLog('ERROR', `Missing chunk ${missingChunks} during reassembly`);
        logBufferStatus(buffer, chunk.id);
        return undefined;
      }

      const completeMessage = buffer.chunks.join('');
      clearMessageBuffer(chunk.id);

      try {
        const parsed = JSON.parse(completeMessage);
        debugLog('COMPLETE', `Successfully reassembled message: ${chunk.id}`);
        return parsed;
      } catch (error) {
        debugLog('ERROR', `Failed to parse reassembled message ${chunk.id}:`, error);
        return undefined;
      }
    }
  } else {
    debugLog('DUPLICATE', `Received duplicate chunk ${chunk.index + 1}/${chunk.total} for message: ${chunk.id}`);
  }

  return undefined;
};

const sendChunkedMessage = async (rawPeer: Peer, message: PlaycastMessage<PlaycastMessageTarget>, channelLabel: string): Promise<void> => {
  const messageData = packMessage(message);
  const totalChunks = Math.ceil(messageData.byteLength / CHUNK_SIZE);
  const messageId = generateMessageId();

  debugLog('SEND', `Starting to send message ${messageId} in ${totalChunks} chunks on channel: ${channelLabel}`);

  const dataChannel = rawPeer.getDataChannel(channelLabel);
  if (!dataChannel) {
    debugLog('ERROR', 'No data channel found');
    return;
  }

  for (let i = 0; i < totalChunks; i++) {
    // Check buffer amount and wait if too high
    while (dataChannel.bufferedAmount > dataChannel.bufferedAmountLowThreshold) {
      debugLog('BUFFER', `Waiting for buffer to clear: ${dataChannel.bufferedAmount} bytes`);
      await new Promise((resolve) => setTimeout(resolve, 1));
    }

    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, messageData.byteLength);
    const chunkData = messageData.slice(start, end);

    let chunk = createBinaryChunk(messageId, i, totalChunks, chunkData);

    debugLog('SEND-CHUNK', `Sending chunk ${i + 1}/${totalChunks} for message: ${messageId}`);
    // ensure the data is ArrayBuffer
    if (!(chunk.buffer instanceof ArrayBuffer)) {
      // convert it
      chunk = new Uint8Array(chunk.buffer);
    }
    const success = rawPeer.send(chunk.buffer as ArrayBuffer, channelLabel);

    if (!success) {
      debugLog('SEND-ERROR', `Failed to send chunk ${i + 1}/${totalChunks} for message: ${messageId}`);
      addToPendingMessages(message, channelLabel);
      break;
    }
  }
};

// Main API functions
export const createMessageChunker = (rawPeer: Peer) => {
  // Set up retry interval if not already running
  if (!retryIntervalId) {
    retryIntervalId = setInterval(() => {
      if (pendingMessages.length === 0) return;

      debugLog('RETRY', `Attempting to resend ${pendingMessages.length} pending messages`);

      const currentPending = [...pendingMessages];
      pendingMessages.length = 0; // Clear array while maintaining reference

      currentPending.forEach((pending) => {
        pending.retry++;

        if (pending.retry >= MAX_RETRIES) {
          debugLog('RETRY-FAILED', 'Message failed after maximum retries:', pending.message);
          return;
        }

        debugLog('RETRY-ATTEMPT', `Retry ${pending.retry}/${MAX_RETRIES} for message on channel: ${pending.channelLabel}`);
        sendMessage(rawPeer)(pending.message, pending.channelLabel);
      });
    }, RETRY_INTERVAL);
  }

  const sendMessage =
    (rawPeer: Peer) =>
    (message: any, channelLabel = 'messages'): void => {
      const messageStr = JSON.stringify(message);
      debugLog('SEND-START', `Preparing to send message of size ${messageStr.length} bytes`);

      // Check max message size after first successful send
      if (!hasDetectedMaxSize) {
        const maxSize = detectMaxMessageSize(rawPeer);
        setChunkSize(maxSize);
        hasDetectedMaxSize = true;
        debugLog('DETECT', 'Max message size detected after first send');
      }

      if (messageStr.length <= CHUNK_SIZE) {
        debugLog('SEND-SMALL', `Sending small message (${messageStr.length} bytes) directly`);
        const success = rawPeer.send(messageStr, channelLabel);
        if (!success) {
          debugLog('SEND-ERROR', 'Failed to send small message, adding to pending');
          addToPendingMessages(message, channelLabel);
        }
        return;
      }

      debugLog('SEND-LARGE', `Message exceeds chunk size, will split into chunks`);
      sendChunkedMessage(rawPeer, message, channelLabel);
    };

  const handleIncomingMessage = (data: any, channelLabel: string): any => {
    debugLog('RECEIVE-START', `Received data on channel: ${channelLabel}`);
    if (data instanceof ArrayBuffer) {
      const chunk = parseBinaryChunk(new Uint8Array(data));
      if (!chunk) {
        debugLog('CHUNK', 'Chunk not parsed correctly');
        return undefined;
      }

      const { header } = chunk;
      debugLog('RECEIVE', `Received chunk ${header.index + 1}/${header.total} for message: ${header.id}`);

      // Initialize or get buffer
      if (!messageBuffers[header.id]) {
        debugLog('BUFFER', 'Creating buffer for message ' + header.id);
        messageBuffers[header.id] = {
          chunks: new Array<Uint8Array<ArrayBuffer> | undefined>(header.total) as Uint8Array<ArrayBuffer>[],
          receivedChunks: 0,
          total: header.total,
          channelLabel,
          timer: setTimeout(() => {
            debugLog('TIMEOUT', `Message ${header.id} timed out`);
            clearMessageBuffer(header.id);
          }, CHUNK_TIMEOUT),
        };
      }

      const buffer = messageBuffers[header.id];
      debugLog('RECEIVE-BUFFER', `Buffer for message ${header.id}:`, buffer);
      buffer.chunks[header.index] = new Uint8Array(chunk.data);
      buffer.receivedChunks++;

      if (buffer.receivedChunks === buffer.total) {
        // First verify all chunks are present
        for (let i = 0; i < buffer.total; i++) {
          if (!buffer.chunks[i]) {
            debugLog('ERROR', `Missing chunk ${i} during reassembly of message ${header.id}`);
            clearMessageBuffer(header.id);
            return undefined;
          }
        }

        // Now we know all chunks are defined, we can safely cast
        const chunks = buffer.chunks as Uint8Array[];

        // Combine all chunks
        const totalSize = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
        const combined = new Uint8Array(totalSize);
        let offset = 0;

        for (const chunk of chunks) {
          combined.set(chunk, offset);
          offset += chunk.byteLength;
        }

        clearMessageBuffer(header.id);

        try {
          return unpackMessage(combined);
        } catch (error) {
          debugLog('ERROR', `Failed to parse reassembled message ${header.id}:`, error);
          return undefined;
        }
      }

      return undefined;
    }
    debugLog('RECEIVE-TYPE', typeof data);

    const parsed = parseMessage(data);
    debugLog('RECEIVE-PARSE', `Parsed message:`, parsed);
    if (!parsed) {
      debugLog('RECEIVE-ERROR', 'Failed to parse incoming message');
      return undefined;
    }

    if (parsed.type === 'chunked') {
      debugLog('RECEIVE-CHUNKED', `Received chunked message part`);
      return handleChunk(parsed.chunk, channelLabel);
    }

    debugLog('RECEIVE-COMPLETE', `Received complete message`);
    return parsed;
  };

  const cleanup = (): void => {
    debugLog('CLEANUP-START', 'Starting cleanup');

    // Clear all message buffers
    Object.keys(messageBuffers).forEach(clearMessageBuffer);

    const pendingCount = pendingMessages.length;
    pendingMessages.length = 0;
    debugLog('CLEANUP-PENDING', `Cleared ${pendingCount} pending messages`);

    // Clear retry interval
    if (retryIntervalId) {
      clearInterval(retryIntervalId);
      retryIntervalId = undefined;
      debugLog('CLEANUP-INTERVAL', 'Cleared retry interval');
    }
    debugLog('CLEANUP-COMPLETE', 'Cleanup finished');
  };

  const setDebug = (enabled: boolean): void => {
    isDebugEnabled = enabled;
    debugLog('CONFIG', `Debug logging ${enabled ? 'enabled' : 'disabled'}`);
  };

  const setChunkSize = (size: number): void => {
    debugLog('CONFIG', `Setting chunk size to ${size} bytes`);
    if (size <= 0) {
      debugLog('ERROR', 'Invalid chunk size, must be greater than 0');
      return;
    }
    CHUNK_SIZE = size;
  };

  return {
    sendMessage: sendMessage(rawPeer),
    handleIncomingMessage,
    cleanup,
    setDebug,
    setChunkSize,
    detectMaxMessageSize: () => detectMaxMessageSize(rawPeer),
  };
};
